import { useState } from 'react';
import { Trash2 } from 'lucide-react';
import type { WhatsappGroup } from '@wix/ai-scheduling-assistant-common-lib/entities';
import { Button, Typography } from '../ui';
import { Modal } from '../../Modal';
import { useTranslation } from '../../../lib/i18n';
import { useGenericBi } from '../../../lib/use-generic-bi';
import { GroupActionError, useWhatsappGroupActions } from '../../settings/shared/settings-queries';
import { GroupSettingsPanel } from './GroupSettingsPanel';
import { WHATSAPP_PAGE } from './constants';
import styles from './GroupSettingsPanel.module.css';

// One WhatsApp group's settings as a centered "Group Settings" modal (Figma 12815-181336) — the
// replacement for the dedicated `channels/whatsapp/group/{id}` sub-page. The DS <Modal> owns the
// titled header and the Close + Delete footer; GroupSettingsPanel fills the body (status badge + name
// with the inline rename, the response-mode switch, the invite-link cards, and the open-conversation
// card). Delete is confirmed behind a second modal; a delete (or the sub-page's push deeplink closing)
// calls onClose.
export function GroupSettingsModal({ group, onClose }: { group: WhatsappGroup; onClose: () => void }) {
  const { t } = useTranslation();
  const { logPageClickBi } = useGenericBi();
  const [confirmDelete, setConfirmDelete] = useState(false);

  return (
    <>
      <Modal
        open
        onClose={onClose}
        size="minimum"
        title={t('settings.whatsapp.group.settings')}
        labelledBy="wa-group-settings-title"
        maxHeightPx={650}
        scrollBody
        headerSeparator
        footerSeparator
        footer={
          <>
            <Button variant="outlined" onClick={onClose}>
              {t('common.close')}
            </Button>
            <Button
              variant="outlined"
              color="destructive"
              startIcon={<Trash2 size={16} aria-hidden />}
              onClick={() => {
                logPageClickBi({ ...WHATSAPP_PAGE, button_name: 'delete_group' });
                setConfirmDelete(true);
              }}
            >
              {t('settings.whatsapp.group.delete')}
            </Button>
          </>
        }
      >
        <GroupSettingsPanel group={group} />
      </Modal>
      {confirmDelete && <DeleteGroupConfirm group={group} onClose={() => setConfirmDelete(false)} onDeleted={onClose} />}
    </>
  );
}

function DeleteGroupConfirm({ group, onClose, onDeleted }: { group: WhatsappGroup; onClose: () => void; onDeleted: () => void }) {
  const { t } = useTranslation();
  const { logModalActionBi } = useGenericBi();
  const { remove } = useWhatsappGroupActions();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const cancel = () => {
    logModalActionBi({ page_name: WHATSAPP_PAGE.page_name, modal_name: 'delete_group', action: 'cancel' });
    onClose();
  };

  const confirm = async () => {
    logModalActionBi({ page_name: WHATSAPP_PAGE.page_name, modal_name: 'delete_group', action: 'confirm' });
    setBusy(true);
    setError(null);
    try {
      await remove(group.id);
      onDeleted();
    } catch (err) {
      setError(err instanceof GroupActionError && err.guard ? err.guard : t('settings.whatsapp.group.delete.error'));
      setBusy(false);
    }
  };

  return (
    <Modal
      open
      onClose={busy ? () => {} : cancel}
      title={t('settings.whatsapp.group.delete.title')}
      labelledBy="wa-delete-title"
      maxHeightPx={650}
      scrollBody
      footer={
        <>
          <Button variant="text" color="standard" onClick={cancel} disabled={busy}>
            {t('common.cancel')}
          </Button>
          <Button variant="contained" color="destructive" onClick={confirm} loading={busy}>
            {t('common.delete')}
          </Button>
        </>
      }
    >
      <Typography variant="p2" color="secondary">
        {t('settings.whatsapp.group.delete.confirm', { subject: group.subject })}
      </Typography>
      {error && (
        <Typography className={styles.error} variant="p2" role="alert">
          {error}
        </Typography>
      )}
    </Modal>
  );
}
